import type { KeyboardEvent, ReactNode } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useReducedMotion } from '@/common/presentation/hooks/useReducedMotion';

export interface LayersRowNameProps {
  chevron?: { expanded: boolean; onToggle: () => void };
  hideChevronSlot?: boolean;
  icon: ReactNode;
  name: string;
  /** Read in place of `name` by assistive technology (the chevron and the select zone); the visible text stays `name`. */
  accessibleName?: string;
  nameContent?: ReactNode;
  hidden?: boolean;
  onSelect?: () => void;
  selected?: boolean;
  /** Accepted for `LayersRowProps` contract parity — see the caveat on `LayersRow`. */
  scopeNote?: string;
  iconTone?: 'variable' | 'owned';
  /** Test id of the name text (`<row testId>:name`). */
  nameTestId?: string;
  /** A stamp on the name's own line, right after it; it keeps its width while a long name truncates. */
  nameSuffix?: ReactNode;
}

/**
 * The row's leading anatomy — the 16px chevron slot (only on a branch row), the type-indicator
 * square, and the name. Rendered whether or not the row is selectable so every row shares one
 * geometry (`.rw-nav-tree__select`-equivalent): `flex: 1; display: flex; align-items: center`.
 *
 * The caret is its OWN target (expand/collapse); the icon + name are the SELECT ZONE — never the
 * whole row, or the chevron would select as it expanded. A field row hands in `nameContent` (its
 * own interactive node), which becomes the keyboard path instead of this zone.
 */
export function LayersRowName({
  chevron,
  hideChevronSlot = false,
  icon,
  name,
  accessibleName,
  nameContent,
  hidden = false,
  onSelect,
  selected = false,
  scopeNote,
  iconTone,
  nameTestId,
  nameSuffix
}: LayersRowNameProps) {
  const selectZoneFocusable = Boolean(onSelect) && !nameContent;
  const reducedMotion = useReducedMotion();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!selectZoneFocusable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect?.();
  };

  return (
    <>
      {hideChevronSlot ? null : (
        <Box aria-hidden={chevron ? undefined : true} sx={{ width: 'var(--size-16)', display: 'inline-flex', flexShrink: 0 }}>
          {chevron ? (
            <IconButton
              size="small"
              disableRipple
              disableFocusRipple
              onClick={chevron.onToggle}
              aria-label={chevron.expanded ? `Collapse ${accessibleName ?? name}` : `Expand ${accessibleName ?? name}`}
              aria-expanded={chevron.expanded}
              sx={{
                width: 'var(--size-16)',
                height: 'var(--size-24)',
                p: 0,
                borderRadius: 'var(--border-radius-none)',
                color: 'var(--icon-neutral)',
                '&:focus-visible': { outline: 'none', boxShadow: 'inset 0 0 0 var(--border-width-sm) var(--border-focus)' }
              }}
            >
              <ChevronRightIcon
                sx={{ fontSize: 'var(--size-16)' }}
                style={{
                  transform: chevron.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: reducedMotion ? 'none' : 'transform var(--duration-fast)'
                }}
              />
            </IconButton>
          ) : null}
        </Box>
      )}

      <Box
        onMouseDown={onSelect}
        role={selectZoneFocusable ? 'button' : undefined}
        tabIndex={selectZoneFocusable ? 0 : undefined}
        aria-label={selectZoneFocusable ? accessibleName : undefined}
        aria-current={onSelect && selected ? true : undefined}
        onKeyDown={selectZoneFocusable ? handleKeyDown : undefined}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--size-8)',
          alignSelf: 'stretch',
          ...(onSelect ? { cursor: 'pointer' } : null),
          '&:focus-visible': { outline: 'none', boxShadow: 'inset 0 0 0 var(--border-width-sm) var(--border-focus)' }
        }}
      >
        <Box
          aria-hidden
          title={scopeNote}
          data-icon-tone={iconTone}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'var(--size-24)',
            height: 'var(--size-24)',
            flexShrink: 0,
            borderRadius: 'var(--border-radius-none)',
            backgroundColor: 'var(--surface-neutral-container-tertiary)',
            color: hidden ? 'var(--icon-disabled)' : 'var(--icon-neutral)'
          }}
        >
          {icon}
        </Box>

        {nameContent ?? (
          <Typography
            component="span"
            noWrap
            title={name}
            data-testid={nameTestId}
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              fontSize: 'var(--font-label-large-size)',
              lineHeight: 'var(--font-label-large-line-height)',
              fontWeight: 'var(--font-label-large-medium-weight)',
              letterSpacing: 'var(--font-label-large-letter-spacing)',
              color: hidden ? 'var(--text-disabled)' : 'var(--text-body)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {name}
          </Typography>
        )}
        {nameSuffix ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{nameSuffix}</Box> : null}
      </Box>
    </>
  );
}
